import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { caregiverProfileService } from '../../services/caregiverProfileService';

interface Service {
  id: string;
  name: string;
  description: string | null;
  duration: number | null;
  price: number | string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ServicesManagementSidebarProps {
  open: boolean;
  onClose: () => void;
  services: Service[];
}

interface ServiceFormData {
  name: string;
  description: string;
  duration: number;
  price: number;
}

const ServicesManagementSidebar: React.FC<ServicesManagementSidebarProps> = ({
  open,
  onClose,
  services,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showServiceDialog, setShowServiceDialog] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [serviceForm, setServiceForm] = useState<ServiceFormData>({
    name: '',
    description: '',
    duration: 60,
    price: 0,
  });

  const handleAddService = () => {
    setEditingService(null);
    setServiceForm({
      name: '',
      description: '',
      duration: 60,
      price: 0,
    });
    setShowServiceDialog(true);
  };

  const handleEditService = (service: Service) => {
    setEditingService(service);
    setServiceForm({
      name: service.name,
      description: service.description || '',
      duration: service.duration || 60,
      price: typeof service.price === 'string' ? parseFloat(service.price) : (service.price || 0),
    });
    setShowServiceDialog(true);
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await caregiverProfileService.deleteService(serviceId);
      setSuccess('Service deleted successfully');
      onClose(); // Refresh the parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete service');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveService = async () => {
    try {
      setLoading(true);
      setError(null);

      if (editingService) {
        await caregiverProfileService.updateService(editingService.id, serviceForm);
        setSuccess('Service updated successfully');
      } else {
        await caregiverProfileService.createService(serviceForm);
        setSuccess('Service created successfully');
      }

      setShowServiceDialog(false);
      onClose(); // Refresh the parent component
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleServiceFormChange = (field: keyof ServiceFormData, value: string | number) => {
    setServiceForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const formatPrice = (price: number | string | null) => {
    if (!price) return 'Not set';
    
    // Convert to number if it's a string
    const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
    
    // Check if it's a valid number
    if (isNaN(numericPrice)) return 'Not set';
    
    return `$${numericPrice.toFixed(2)}`;
  };

  const formatDuration = (duration: number | string | null) => {
    if (!duration) return 'Not set';
    
    // Convert to number if it's a string
    const numericDuration = typeof duration === 'string' ? parseInt(duration) : duration;
    
    // Check if it's a valid number
    if (isNaN(numericDuration)) return 'Not set';
    
    const hours = Math.floor(numericDuration / 60);
    const minutes = numericDuration % 60;
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  const isValid = serviceForm.name.trim() && 
                  serviceForm.description.trim() && 
                  serviceForm.duration >= 15 && 
                  serviceForm.price > 0;

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: isMobile ? '100%' : '40%',
            maxWidth: 600,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Manage Services</Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Add and manage the services you offer to patients.
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddService}
              fullWidth
              sx={{ mb: 3 }}
            >
              Add New Service
            </Button>

            {services.length > 0 ? (
              <List>
                {services.map((service) => (
                  <ListItem key={service.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{service.name}</Typography>
                          {!service.isActive && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {service.description}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatDuration(service.duration)} • {formatPrice(service.price)}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditService(service)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteService(service.id)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No services added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add New Service" to get started
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Service Form Dialog */}
      <Dialog
        open={showServiceDialog}
        onClose={() => setShowServiceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingService ? 'Edit Service' : 'Add New Service'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              fullWidth
              label="Service Name"
              value={serviceForm.name}
              onChange={(e) => handleServiceFormChange('name', e.target.value)}
              placeholder="e.g., Physical Therapy Session"
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={serviceForm.description}
              onChange={(e) => handleServiceFormChange('description', e.target.value)}
              placeholder="Describe what this service includes and what patients can expect"
            />

            <FormControl fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select
                value={serviceForm.duration}
                onChange={(e) => handleServiceFormChange('duration', e.target.value as number)}
                label="Duration"
              >
                <MenuItem value={15}>15 minutes</MenuItem>
                <MenuItem value={30}>30 minutes</MenuItem>
                <MenuItem value={45}>45 minutes</MenuItem>
                <MenuItem value={60}>1 hour</MenuItem>
                <MenuItem value={90}>1.5 hours</MenuItem>
                <MenuItem value={120}>2 hours</MenuItem>
                <MenuItem value={180}>3 hours</MenuItem>
                <MenuItem value={240}>4 hours</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Price ($)"
              value={serviceForm.price}
              onChange={(e) => handleServiceFormChange('price', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              placeholder="0.00"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowServiceDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSaveService}
            disabled={loading || !isValid}
          >
            {loading ? 'Saving...' : editingService ? 'Update Service' : 'Add Service'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ServicesManagementSidebar;
